import React, { useRef, useState, useEffect } from 'react';

function GameSoundController({ roomData }) {
  const previousState = useRef(null); // To track the previous state
  const [playedStates, setPlayedStates] = useState({
    flop: false,
    turn: false,
    river: false,
  }); // To track if the sound has been played for each state
  const previousParticipants = useRef([]);

  useEffect(() => {
    if (roomData?.turn) {
      const currentState = roomData.turn;

      // Reset playedStates if the turn changes to preflop
      if (currentState === 'preflop' && previousState.current !== 'preflop') {
        setPlayedStates({
          flop: false,
          turn: false,
          river: false,
        });
        previousState.current = currentState; // Update the previous state
        return; // Exit early since no sound needs to be played for preflop
      }

      // Check if the state has changed to flop, turn, or river and play sound only once
      if (
        (currentState === 'flop' && !playedStates.flop) ||
        (currentState === 'turn' && !playedStates.turn) ||
        (currentState === 'river' && !playedStates.river)
      ) {
        // Play the sound
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const audio = new Audio('sounds/throwingCards.mp3');
        const track = audioContext.createMediaElementSource(audio);
        track.connect(audioContext.destination);
        audio.play().catch((error) => {
          console.error('Error playing sound:', error);
        });

        // Mark the state as played
        setPlayedStates((prev) => ({
          ...prev,
          [currentState]: true,
        }));
      }

      // Update the previous state only if it has changed
      if (previousState.current !== currentState) {
        previousState.current = currentState;
      }
    }
  }, [roomData]); // Remove playedStates from the dependency array

  useEffect(() => {
    if (roomData?.participants) {
      roomData.participants.forEach((participant, index) => {
        const previousParticipant = previousParticipants.current[index];

        // Check if currentPlayer turned true and then false, and action is 'check'
        if (
          previousParticipant &&
          previousParticipant.currentPlayer === true &&
          participant.currentPlayer === false &&
          participant.action === 'check'
        ) {
          // Play the check sound
          const audio = new Audio('sounds/check.mp3');
          audio.play().catch((error) => {
            console.error('Error playing check sound:', error);
          });
        }

        // Check if currentPlayer turned true and then false, and action is 'bet', 'raise', or 'call'
        if (
          previousParticipant &&
          previousParticipant.currentPlayer === true &&
          participant.currentPlayer === false &&
          ['bet', 'raise', 'call'].includes(participant.action)
        ) {
          // Play the bet/raise/call sound
          const audio = new Audio('sounds/betCall.mp3');
          audio.play().catch((error) => {
            console.error('Error playing bet/raise/call sound:', error);
          });
        }

        // Check if currentPlayer turned true and then false, and action is 'allIn'
        if (
          previousParticipant &&
          previousParticipant.currentPlayer === true &&
          participant.currentPlayer === false &&
          participant.action === 'allIn'
        ) {
          // Play the all-in sound
          const audio = new Audio('sounds/allIn.mp3');
          audio.play().catch((error) => {
            console.error('Error playing all-in sound:', error);
          });
        }

        // Check if currentPlayer turned true and then false, and action is 'fold'
        if (
          previousParticipant &&
          previousParticipant.currentPlayer === true &&
          participant.currentPlayer === false &&
          participant.action === 'fold'
        ) {
          // Play the fold sound
          const audio = new Audio('sounds/fold.mp3');
          audio.play().catch((error) => {
            console.error('Error playing fold sound:', error);
          });
        }
      });

      // Update the previous participants state
      previousParticipants.current = roomData.participants.map((p) => ({ ...p }));
    }
  }, [roomData?.participants]);

  return null; // This component doesn't render anything
}

export default GameSoundController;